import {
  createSelector,
  createSlice,
  isAnyOf,
  PayloadAction,
} from '@reduxjs/toolkit';
import type { Dispatch } from 'redux';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {
  SOURCE_ADMIN_SMS,
  SOURCE_TCAPI,
} from '@rsos/constants/locationSources';
import {
  CONVERSATION_END,
  CONVERSATION_START,
  CONVERSATION_UPDATE,
  type BaseConversation,
} from '@rsos/hermes-js/src/typings/conversation';
import { User } from '@rsos/utils/useTalkJS';
import {
  ConversationErrorRecord,
  ConversationLoadingRecord,
  conversationStateValues,
} from './conversations.typings';
import { invalidateTCAPIConversationMetadata, tcapiAPI } from './tcapiAPI';
import { getIsConversationEnded } from './utils';

/**
 * A Redux slice that contains and bundles all possible conversations and their data together
 * Only stores 1 main conversation at a time
 */
const conversationsSlice = createSlice({
  name: 'conversations',
  initialState: {} as Record<string, conversationStateValues>,
  reducers: {
    setConversationUnread: (
      state,
      action: PayloadAction<{
        isUnread: boolean;
        messageID?: string;
        callerID: string | null | undefined;
      }>,
    ) => {
      const { isUnread, callerID } = action.payload;

      if (!callerID) return;

      if (state[callerID]) {
        state[callerID] = {
          ...state[callerID],
          isUnread: isUnread,
        };
      }
    },
    setIsConversationCreated: (
      state,
      action: PayloadAction<string | undefined | null>,
    ) => {
      const callerID = action.payload;

      if (!callerID) return;

      const conversation = state[callerID];

      if (conversation && conversation.data) {
        if (conversation.source === SOURCE_TCAPI) {
          conversation.data.status = 'active';
        }
      }
    },
    setIsConversationEnded: (
      state,
      action: PayloadAction<string | undefined | null>,
    ) => {
      const callerID = action.payload;

      if (!callerID) return;

      const conversation = state[callerID];

      if (conversation && conversation.data) {
        if (conversation.source === SOURCE_TCAPI) {
          conversation.data.status = 'ended';
        }
      }
    },
    addConversation: (
      state,
      action: PayloadAction<BaseConversation['body']>,
    ) => {
      const body = action.payload;
      const callerID = body?.caller_id;

      if (!callerID) return;

      const hasPreviousConversation = !!state[callerID]?.data;

      state[callerID] = {
        data: body,
        source: body.source,
        isUnread: hasPreviousConversation,
        error: {} as ConversationErrorRecord,
        loading: {} as ConversationLoadingRecord,
        recentMessageType: CONVERSATION_START,
        participants: [],
      } as conversationStateValues;
    },
    updateConversation: (
      state,
      action: PayloadAction<{
        body: BaseConversation['body'];
        messageType: typeof CONVERSATION_UPDATE | typeof CONVERSATION_END;
      }>,
    ) => {
      const body = action.payload.body;
      const callerID = body?.caller_id;

      if (!callerID) {
        // eslint-disable-next-line no-console -- logging for sentry
        console.error(
          'updateConversation: WS message body had no caller_id; ignoring.',
        );
        return;
      }

      if (!state[callerID]) {
        // eslint-disable-next-line no-console -- logging for sentry
        console.error(
          `updateConversation: no conversation in local state for callerID ${callerID}; ignoring.`,
        );
        return;
      }

      // For TCAPI, RTK Query (TCAPIMetaData) is the source of truth for
      // session state. WS update/end messages are broadcast on the caller-id
      // topic and may carry a sibling agency's conversation_id (during
      // transfer flows), which would corrupt the local view if spread into
      // `data`. Take recentMessageType from the message — needed by toast and
      // unread logic — and let RTK Query refresh `data` after the dispatch
      // site invalidates the TCAPIMetaData tag.
      if (state[callerID].source === SOURCE_TCAPI) {
        state[callerID].recentMessageType = action.payload.messageType;
        return;
      }

      state[callerID] = {
        ...state[callerID],
        recentMessageType: action.payload.messageType,
        data: {
          ...state[callerID].data,
          ...body,
        },
      } as conversationStateValues;
    },
    clearConversation: (state, action: PayloadAction<string>) => {
      const callerID = action.payload;
      delete state[callerID];
    },
    addParticipant: (
      state,
      action: PayloadAction<{
        callerID?: string | null;
        participant: User;
      }>,
    ) => {
      const { callerID, participant } = action.payload;

      if (!callerID || !state[callerID]) {
        return;
      }

      const conversation = state[callerID];

      // Ensure participants array exists
      if (!conversation.participants) {
        conversation.participants = [];
      }

      if (conversation.participants.find(p => p.id === participant.id)) {
        return;
      }

      conversation.participants = [...conversation.participants, participant];
    },
  },
  extraReducers: builder => {
    builder
      .addMatcher(
        tcapiAPI.endpoints.getTCAPIConversationMetadata.matchFulfilled,
        (state, action) => {
          const callerID: string = action.meta.arg.originalArgs.callerID;
          const existingConversation = state[callerID];

          // A non-TCAPI conversation (e.g. admin_sms, delivered via websocket)
          // already owns this callerID. With keepUnusedDataFor: Infinity a
          // stale TCAPI session stays cached and could clobber the live entry,
          // so only let the TCAPI session replace it when the TCAPI session is
          // actually newer; otherwise keep the existing conversation.
          if (
            existingConversation &&
            existingConversation.source !== SOURCE_TCAPI
          ) {
            const existingTime =
              existingConversation.data?.conversation_start_time ?? 0;
            const incomingTime =
              action.payload?.creation_time ??
              action.payload?.last_updated_time ??
              0;
            if (incomingTime <= existingTime) {
              return;
            }
          }

          // Replace a stale ended TCAPI session with new active/pending session data
          // This prevents old ended sessions from blocking new sessions on the same number
          if (
            existingConversation?.source === SOURCE_TCAPI &&
            getIsConversationEnded(existingConversation) &&
            !getIsConversationEnded({
              source: SOURCE_TCAPI,
              data: action.payload,
            } as conversationStateValues)
          ) {
            delete state[callerID];
          }

          // Keep ended sessions in state to allow UI to hide buttons and show "Closed Chat" status
          state[callerID] = {
            ...state[callerID],
            source: SOURCE_TCAPI,
            data: action.payload,
            transfer: action.payload?.transfer ?? undefined,
            loading: {
              ...state?.[callerID]?.loading,
              getConversationMetadata: false,
            },
          };
        },
      )
      .addMatcher(
        tcapiAPI.endpoints.getTCAPIConversationMetadata.matchPending,
        (state, action) => {
          const callerID: string = action.meta.arg.originalArgs.callerID;

          // A non-TCAPI conversation (e.g. admin_sms) already owns this
          // callerID — a spurious in-flight TCAPI metadata fetch must not flip
          // its source to tcapi or null its data while loading.
          if (state[callerID] && state[callerID].source !== SOURCE_TCAPI) {
            return;
          }

          state[callerID] = {
            ...state?.[callerID],
            source: SOURCE_TCAPI,
            data: null,
            error: {
              ...state?.[callerID]?.error,
              getConversationMetadata: null,
            },
            loading: {
              ...state?.[callerID]?.loading,
              getConversationMetadata: true,
            },
            isUnread: false,
          };
        },
      )
      .addMatcher(
        tcapiAPI.endpoints.getTCAPIConversationMetadata.matchRejected,
        (state, action) => {
          const callerID: string = action.meta.arg.originalArgs.callerID;

          // NOTE: We intentionally delete the conversation entry here rather than following
          // the typical pattern of preserving the entry with error state. This is because:
          // 1. A 404 response indicates the TCAPI session legitimately doesn't exist (not a temporary error)
          // 2. Stale TCAPI state can persist from previous sessions (e.g., Redux persist or page state)
          // 3. We need conversationSource to be falsy so CallChats.tsx renders the correct component
          //    (TF911ChatBox for TF911 sessions, not ConversationsChatBox)
          // 4. Keeping source="tcapi" with data=null would cause incorrect routing to TCAPI APIs
          //    for non-TCAPI sessions
          // By deleting the entry, we allow the UI to fall back to the appropriate chat component
          // based on the actual session type (TF911, RCS, etc.)
          //
          // Only delete TCAPI-owned entries: a non-TCAPI conversation (e.g.
          // admin_sms) on this callerID must survive a spurious TCAPI 404.
          if (state[callerID] && state[callerID].source !== SOURCE_TCAPI) {
            return;
          }
          delete state[callerID];
        },
      )
      .addMatcher(
        tcapiAPI.endpoints.createTCAPIConversation.matchPending,
        (state, action) => {
          const { callerID } = action.meta.arg.originalArgs;
          if (state[callerID]) {
            state[callerID].loading.createConversation = true;
          }
        },
      )
      .addMatcher(
        isAnyOf(
          tcapiAPI.endpoints.createTCAPIConversation.matchFulfilled,
          tcapiAPI.endpoints.createTCAPIConversation.matchRejected,
        ),
        (state, action) => {
          const { callerID } = action.meta.arg.originalArgs;
          if (state[callerID]) {
            state[callerID].loading.createConversation = false;
          }
        },
      );
  },
});

export function selectConversation(callerId: string) {
  return (state: {
    conversations: Record<string, conversationStateValues>;
  }) => {
    return state.conversations?.[callerId];
  };
}

export function selectConversationId(callerId: string) {
  return createSelector(
    [selectConversation(callerId)],
    (conversation?: conversationStateValues) => {
      if (!conversation) {
        return undefined;
      }
      switch (conversation.source) {
        case SOURCE_ADMIN_SMS:
        case SOURCE_TCAPI:
          return conversation.data?.conversation_id;
      }
    },
  );
}

export function selectConversationUnread(callerId: string) {
  return createSelector(
    [selectConversation(callerId)],
    conversation => conversation?.isUnread,
  );
}

const persistConfig = {
  key: 'conversation',
  storage,
};

export const conversationsReducer = conversationsSlice.reducer;

export const {
  setConversationUnread,
  setIsConversationCreated,
  setIsConversationEnded,
  addConversation,
  clearConversation,
  updateConversation,
  addParticipant,
} = conversationsSlice.actions;

/**
 * Apply a conversation lifecycle update from a websocket message.
 *
 * Encapsulates the invariant that handling a TCAPI lifecycle event also
 * invalidates the TCAPIMetaData RTK Query cache for that callerID. The slice
 * deliberately does not spread the WS body into `data` for TCAPI sources
 * (the body is broadcast on the caller-id topic and may carry a sibling
 * agency's conversation_id during transfer flows); the cache invalidation
 * here is what triggers a re-fetch of the authoritative session for our
 * orgName, and matchFulfilled hydrates `data` with the correct payload.
 */
export function applyConversationUpdate(payload: {
  body: BaseConversation['body'];
  messageType: typeof CONVERSATION_UPDATE | typeof CONVERSATION_END;
}) {
  return (dispatch: Dispatch) => {
    dispatch(updateConversation(payload));
    if (payload.body.source === SOURCE_TCAPI && payload.body.caller_id) {
      dispatch(invalidateTCAPIConversationMetadata(payload.body.caller_id));
    }
  };
}

/**
 * Receive a TCAPI soft transfer addressed to our agency from a websocket
 * message.
 *
 * The WS message is treated as a "session changed, refetch" trigger only —
 * transfer state itself comes from the TCAPI session GET response, which
 * matchFulfilled hydrates into both ``data`` and ``transfer``. With
 * ``keepUnusedDataFor: Infinity`` a prior session for the same caller can
 * leave a populated cache in place, so the next subscription returns cached
 * data without dispatching matchFulfilled. Invalidating the TCAPIMetaData
 * tag forces the next subscription to hit the network so the receiver
 * picks up the authoritative session.
 */
export function applyReceivedTransfer(callerID: string) {
  return (dispatch: Dispatch) => {
    dispatch(invalidateTCAPIConversationMetadata(callerID));
  };
}

const persistedReducer = persistReducer(
  persistConfig,
  conversationsSlice.reducer,
);

export default persistedReducer;
